import { useMemo } from 'react';

import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import {
  areIntervalsOverlapping,
  compareAsc,
  differenceInDays,
  getOverlappingDaysInIntervals,
} from 'date-fns';

import {
  Application,
  FullValidatedWorkClaimProps,
  ReviewedFullCandidateProps,
  TrancheStatus,
} from 'types';

import { ReviewedCandidateSummaryRow } from './ReviewedCandidateSummaryRow';

export const ReviewedCandidateSummaryTable = (props: {
  applications: Array<Application>;
  validated_work_claims: Array<FullValidatedWorkClaimProps>;
  trancheStatus: TrancheStatus | undefined;
  setCandidate: React.Dispatch<
    React.SetStateAction<ReviewedFullCandidateProps>
  >;
}) => {
  const { applications, validated_work_claims, trancheStatus, setCandidate } =
    props;
  const summaryData = useMemo(() => {
    return (
      applications
        .map((review) => {
          // find all work claims that match this application
          const workClaimSummary = validated_work_claims
            .filter(
              (claim) => claim?.organization?.id === review.organization_id,
            )
            //ensure claims are sorted by start date
            .sort((a, b) =>
              compareAsc(
                a.validated_work_claim_start_date,
                b.validated_work_claim_start_date,
              ),
            )
            .reduce(
              (acc: any, curr: FullValidatedWorkClaimProps) => {
                acc.hasClaims = true;
                const currInterval = {
                  start: curr.validated_work_claim_start_date,
                  end: curr.validated_work_claim_end_date,
                };
                const currDiff =
                  differenceInDays(currInterval.end, currInterval.start) + 1;

                //if this is the first iteration set interval to current interval
                if (!acc.interval) {
                  acc.workDays = 0;
                }
                if (
                  acc.interval &&
                  areIntervalsOverlapping(acc.interval, currInterval)
                ) {
                  //claims are sorted by start date so if current interval end date > last end date (and intervals overlap) add the difference to workdays and set current interval to cover both periods
                  if (compareAsc(acc.interval.end, currInterval.end)) {
                    acc.workDays +=
                      differenceInDays(currInterval.end, currInterval.start) -
                      getOverlappingDaysInIntervals(acc.interval, currInterval);
                    //accumulater interval set to last iterations start date and this iterations end date to cover overlapping intervals
                    acc.interval.end = currInterval.end;
                  }
                } else {
                  //if intervals dont overlap then make new interval current interval and add the difference to total
                  acc.interval = currInterval;
                  acc.workDays += currDiff;
                }
                if (!acc.derog && curr.has_derog) {
                  acc = { ...acc, ...{ derog: true } };
                }
                return acc;
              },
              { derog: false, interval: null, workDays: 0, hasClaims: false },
            );
          return {
            reviewStatusId: review.organization_id,
            candidateId: review.candidate_id,
            organization: review.organization,
            status: review.review_status,
            comment: review.review_status_comment,
            hasClaims: workClaimSummary.hasClaims,
            derog: workClaimSummary.derog,
            workDays: workClaimSummary.workDays,
            reportedBy: review.reviewer,
            reportedDate: review.review_time,
          };
        })
        //A candidate submission creates a organization applicant link for the parent as well as any children that might exist for that parent.
        //Here we want to filter out all data from child applicant links unless they are already assocated with a claim or child claim is unique and not already reflected in the current set of orgs.
        .filter((value, index, array) => {
          const orgs = array.map((d) => d.organization?.id);
          const parentId = value.organization?.parent_id || false;
          const specialChild = parentId ? !orgs.includes(parentId) : false;
          return specialChild || value.hasClaims;
        })
    );
  }, [applications, validated_work_claims]);
  return (
    <TableContainer component={Paper}>
      <Table aria-label='work claim summary table'>
        <TableHead>
          <TableRow key='header' sx={{ backgroundColor: '#f0f0f0' }}>
            <TableCell>Reported Employer</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Comments</TableCell>
            <TableCell>Days</TableCell>
            <TableCell>Derog</TableCell>
            <TableCell>Reported By</TableCell>
            <TableCell>Date Reported</TableCell>
            <TableCell>Unsubmit?</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summaryData.map((summary) => {
            return (
              <ReviewedCandidateSummaryRow
                key={summary.reviewStatusId}
                summary={summary}
                trancheStatus={trancheStatus}
                setCandidate={setCandidate}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
